import { useState, useEffect } from 'react';
import { getAllRateLimiterStats } from '../utils/rateLimiter';
import { getAllFetchStatus } from '../utils/smartPolling';
import { useTranslation } from '../hooks/useTranslation';

/**
 * Compact API Status Indicator for the main dashboard
 */
export default function ApiStatusIndicator() {
  const { t } = useTranslation();
  const [rateLimitStats, setRateLimitStats] = useState([]);
  const [fetchStatus, setFetchStatus] = useState([]);

  useEffect(() => {
    const updateStats = () => {
      setRateLimitStats(getAllRateLimiterStats());
      setFetchStatus(getAllFetchStatus());
    };

    updateStats();
    const interval = setInterval(updateStats, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, []);

  // Calculate overall health
  const getOverallHealth = () => {
    const criticalApis = rateLimitStats.filter(stat => 
      ['morpho', 'coingecko'].includes(stat.name.toLowerCase())
    );
    
    const anyNearLimit = criticalApis.some(stat => stat.daily.percentUsed >= 80);
    const anyBlocked = fetchStatus.some(status => !status.canFetch);
    
    if (anyNearLimit) return 'warning';
    if (anyBlocked) return 'error';
    return 'healthy';
  };

  const health = getOverallHealth();
  const healthColors = {
    healthy: 'text-green-600 bg-green-100',
    warning: 'text-yellow-600 bg-yellow-100',
    error: 'text-red-600 bg-red-100'
  };

  const healthIcons = {
    healthy: '✅',
    warning: '⚠️',
    error: '🚫'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
          {t('apiStatus.title')}
        </h3>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${healthColors[health]}`}>
          <span className="mr-1">{healthIcons[health]}</span>
          {health === 'healthy' ? t('apiStatus.allGood') : health === 'warning' ? t('apiStatus.nearLimit') : t('apiStatus.rateLimited')}
        </span>
      </div>

      <div className="space-y-2">
        {rateLimitStats.slice(0, 3).map(stat => (
          <div key={stat.name} className="flex justify-between items-center text-xs">
            <span className="text-gray-600 dark:text-gray-400 capitalize">
              {stat.name}
            </span>
            <div className="flex items-center gap-2">
              <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                <div 
                  className={`h-1.5 rounded-full transition-all ${
                    stat.daily.percentUsed >= 90 ? 'bg-red-500' :
                    stat.daily.percentUsed >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(stat.daily.percentUsed, 100)}%` }}
                />
              </div>
              <span className="text-gray-500 dark:text-gray-400 w-8 text-right">
                {stat.daily.percentUsed.toFixed(0)}%
              </span>
            </div>
          </div>
        ))}
      </div>

      {rateLimitStats.length > 3 && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
          +{rateLimitStats.length - 3} {t('apiStatus.moreApis')}
        </div>
      )}

      <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <div className="flex justify-between">
            <span>{t('apiStatus.nextRefresh')}</span>
            <span>
              {fetchStatus.find(s => s.api === 'morpho')?.nextFetchIn > 0 
                ? `${fetchStatus.find(s => s.api === 'morpho')?.nextFetchIn}s`
                : t('apiStatus.ready')
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
